import React, { useState, useEffect } from 'react';
import { getAllTeamMembers, updateMemberRole, deactivateMember, reactivateMember } from '../utils/api';

const ROLES = ['admin', 'full', 'regular', 'view'];

const ROLE_META = {
  admin:   { label: 'Admin',   cls: 'badge-red',    desc: 'Full access + user management' },
  full:    { label: 'Full',    cls: 'badge-blue',   desc: 'Full CRUD on all records' },
  regular: { label: 'Regular', cls: 'badge-green',  desc: 'Edit own contacts only' },
  view:    { label: 'View',    cls: 'badge-neutral', desc: 'Read-only access' },
};

function relTime(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const diff = Date.now() - d;
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 60)   return `${mins}m ago`;
  if (hours < 24)  return `${hours}h ago`;
  if (days < 30)   return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function TeamManagementPage({ meData }) {
  const [members,  setMembers]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [saving,   setSaving]   = useState(null); // id of member being saved
  const [confirm,  setConfirm]  = useState(null); // { id, name, action }

  const load = () => {
    setLoading(true);
    getAllTeamMembers()
      .then(r => setMembers(r.data || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleRoleChange = async (id, role) => {
    setSaving(id);
    try {
      const updated = await updateMemberRole(id, role);
      setMembers(prev => prev.map(m => m.id === id ? { ...m, ...updated } : m));
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(null);
    }
  };

  const handleDeactivate = async () => {
    if (!confirm) return;
    setSaving(confirm.id);
    setConfirm(null);
    try {
      const updated = await deactivateMember(confirm.id);
      setMembers(prev => prev.map(m => m.id === confirm.id ? { ...m, ...updated } : m));
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(null);
    }
  };

  const handleReactivate = async (id) => {
    setSaving(id);
    try {
      const updated = await reactivateMember(id);
      setMembers(prev => prev.map(m => m.id === id ? { ...m, ...updated } : m));
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(null);
    }
  };

  if (!meData || meData.role !== 'admin') {
    return (
      <div className="page-container">
        <div className="empty-state">
          <p>Admin access required to view this page.</p>
        </div>
      </div>
    );
  }

  const active   = members.filter(m => m.is_active);
  const inactive = members.filter(m => !m.is_active);

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Team management</h1>
          <p className="page-subtitle">{active.length} active · {inactive.length} inactive</p>
        </div>
      </div>

      {/* Role reference */}
      <div className="card" style={{ padding: '12px 16px', marginBottom: 20 }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: '0.07em', color: 'var(--color-text-muted)', marginBottom: 8 }}>
          Role reference
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
          {ROLES.map(r => (
            <div key={r} style={{ fontSize: '0.8rem' }}>
              <span className={`badge ${ROLE_META[r].cls}`} style={{ marginRight: 6 }}>
                {ROLE_META[r].label}
              </span>
              <span style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>
                {ROLE_META[r].desc}
              </span>
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div style={{ background: 'var(--color-danger-light)', border: '1px solid var(--color-danger)',
          borderRadius: 8, padding: '10px 14px', marginBottom: 16,
          fontSize: '0.85rem', color: 'var(--color-danger)' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="page-loading">Loading team members…</div>
      ) : (
        <>
          {/* Active members */}
          <div style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.08em', color: 'var(--color-text-muted)', marginBottom: 8 }}>
            Active ({active.length})
          </div>
          <div className="card" style={{ marginBottom: 20 }}>
            {active.length === 0 ? (
              <div style={{ padding: 16, color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
                No active members.
              </div>
            ) : (
              active.map((m, i) => (
                <div key={m.id} style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 160px 120px 80px',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  borderBottom: i < active.length - 1 ? '1px solid var(--color-border-light)' : 'none',
                  opacity: saving === m.id ? 0.5 : 1,
                  transition: 'opacity 0.15s',
                }}>
                  {/* Identity */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%',
                        background: 'var(--color-accent-light)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-accent)',
                        flexShrink: 0 }}>
                        {m.display_name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          {m.display_name}
                          {m.id === meData.id && (
                            <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)',
                              marginLeft: 6 }}>(you)</span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                          {m.email}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Last login */}
                  <div>
                    {m.last_login_at ? (
                      <div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                          {relTime(m.last_login_at)}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
                          last login
                        </div>
                      </div>
                    ) : (
                      <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)',
                        background: 'var(--color-warning-light)', padding: '2px 8px',
                        borderRadius: 10, border: '1px solid var(--color-warning)' }}>
                        Never logged in
                      </span>
                    )}
                  </div>

                  {/* Stats */}
                  <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                    <div>{m.contact_count || 0} contacts</div>
                    <div>{m.interaction_count || 0} interactions</div>
                  </div>

                  {/* Role + actions */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                    <select
                      value={m.role}
                      disabled={saving === m.id || m.id === meData.id}
                      onChange={e => handleRoleChange(m.id, e.target.value)}
                      style={{ fontSize: '0.78rem', padding: '3px 6px', borderRadius: 4,
                        border: '1px solid var(--color-border)', fontFamily: 'var(--font-ui)',
                        background: 'var(--color-surface)', color: 'var(--color-text-primary)',
                        cursor: m.id === meData.id ? 'not-allowed' : 'pointer' }}
                    >
                      {ROLES.map(r => (
                        <option key={r} value={r}>{ROLE_META[r].label}</option>
                      ))}
                    </select>
                    {m.id !== meData.id && (
                      <button
                        onClick={() => setConfirm({ id: m.id, name: m.display_name, action: 'deactivate' })}
                        disabled={saving === m.id}
                        style={{ fontSize: '0.72rem', color: 'var(--color-danger)',
                          background: 'none', border: 'none', cursor: 'pointer',
                          padding: 0, fontFamily: 'var(--font-ui)', textDecoration: 'underline' }}
                      >
                        Deactivate
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Inactive members */}
          {inactive.length > 0 && (
            <>
              <div style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.08em', color: 'var(--color-text-muted)', marginBottom: 8 }}>
                Inactive ({inactive.length})
              </div>
              <div className="card">
                {inactive.map((m, i) => (
                  <div key={m.id} style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 160px 120px 80px',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    borderBottom: i < inactive.length - 1 ? '1px solid var(--color-border-light)' : 'none',
                    opacity: 0.6,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%',
                        background: 'var(--color-border)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)',
                        flexShrink: 0 }}>
                        {m.display_name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: '0.9rem',
                          color: 'var(--color-text-muted)', textDecoration: 'line-through' }}>
                          {m.display_name}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                          {m.email}
                        </div>
                      </div>
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                      {m.last_login_at ? relTime(m.last_login_at) : 'Never logged in'}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                      {m.contact_count || 0} contacts
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <button
                        onClick={() => handleReactivate(m.id)}
                        disabled={saving === m.id}
                        className="btn"
                        style={{ fontSize: '0.75rem', padding: '3px 10px' }}
                      >
                        Reactivate
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Deactivate confirmation modal */}
      {confirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--color-surface)', borderRadius: 12,
            border: '1px solid var(--color-border)', padding: '28px 32px', width: 360 }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 8 }}>
              Deactivate {confirm.name}?
            </h2>
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginBottom: 24, lineHeight: 1.5 }}>
              This removes their access immediately. Their contacts, deals, and interaction history
              are preserved. You can reactivate them at any time.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setConfirm(null)}>Cancel</button>
              <button
                onClick={handleDeactivate}
                style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid var(--color-danger)',
                  background: 'var(--color-danger)', color: '#fff', cursor: 'pointer',
                  fontSize: '0.85rem', fontFamily: 'var(--font-ui)' }}>
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
